import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom';

const SOURCE_COLORS = {
  'Web': '#3B82F6',
  'Google': '#F59E0B',
  'Referral': '#22C55E',
  'Facebook': '#8B5CF6',
  'Instagram': '#EC4899',
  'Phone': '#14B8A6',
  'Other': '#94A3B8',
};

const LeadSourcesWidget = ({ orgId }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalLeads, setTotalLeads] = useState(0);
  const [convertedLeads, setConvertedLeads] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!orgId) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const { data } = await supabase?.from('leads')?.select('source, status')?.eq('org_id', orgId)?.is('deleted_at', null);

        const all = data || [];
        setTotalLeads(all?.length);
        setConvertedLeads(all?.filter(l => l?.status === 'converted')?.length);

        const sourceMap = {};
        all?.forEach(l => {
          const src = l?.source || 'Other';
          if (!sourceMap?.[src]) sourceMap[src] = { total: 0, converted: 0 };
          sourceMap[src].total++;
          if (l?.status === 'converted') sourceMap[src].converted++;
        });

        const sorted = Object.entries(sourceMap)?.map(([name, d]) => ({
            name,
            total: d?.total,
            converted: d?.converted,
            rate: d?.total > 0 ? Math.round((d?.converted / d?.total) * 100) : 0,
            color: SOURCE_COLORS?.[name] || '#94A3B8',
          }))?.sort((a, b) => b?.total - a?.total)?.slice(0, 5);

        setSources(sorted);
      } catch (err) {
        console.error('Lead sources error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [orgId]);

  const conversionRate = totalLeads > 0 ? Math.round((convertedLeads / totalLeads) * 100) : 0;

  return (
    <motion.div
      className="bg-card border border-border rounded-2xl p-5 shadow-sm"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-foreground">Lead Sources</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{conversionRate}% overall conversion</p>
        </div>
        <button onClick={() => navigate('/leads')} className="text-xs text-primary hover:underline font-medium">View all →</button>
      </div>
      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4]?.map(i => <div key={i} className="h-8 bg-muted animate-pulse rounded-lg" />)}
        </div>
      ) : sources?.length === 0 ? (
        <div className="text-center py-6">
          <Icon name="UserPlus" size={28} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No leads yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sources?.map(s => (
            <div key={s?.name}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s?.color }} />
                  <span className="text-xs font-medium text-foreground">{s?.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">{s?.total} leads</span>
                  <span className="text-xs font-semibold" style={{ color: s?.rate >= 30 ? '#22C55E' : s?.rate >= 15 ? '#F59E0B' : '#94A3B8' }}>
                    {s?.rate}%
                  </span>
                </div>
              </div>
              <div className="w-full bg-muted rounded-full h-1.5">
                <div
                  className="h-1.5 rounded-full transition-all duration-700"
                  style={{ width: `${totalLeads > 0 ? (s?.total / totalLeads) * 100 : 0}%`, backgroundColor: s?.color }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      {totalLeads > 0 && (
        <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{totalLeads} total leads</span>
          <span className="text-xs font-semibold text-green-500">{convertedLeads} converted</span>
        </div>
      )}
    </motion.div>
  );
};

export default LeadSourcesWidget;
